import { useState, useEffect } from 'react';
import { giftsApi } from '../../api/gifts';

// ── Theme ──────────────────────────────────────────────────
const C = {
  navy: '#1B2A4A', navyMuted: '#3A5080', burgundy: '#7A2E3B',
  gold: '#B8953E', hunter: '#2D5F4A', ink: '#1A1A1A',
  textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6',
  border: '#D6CCBE', borderLight: '#E8E0D4',
};

function fmt(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v); const neg = v < 0;
  if (abs >= 1e6) return `${neg ? '(' : ''}$${(abs / 1e6).toFixed(1)}M${neg ? ')' : ''}`;
  if (abs >= 1000) return `${neg ? '(' : ''}$${(abs / 1000).toFixed(0)}K${neg ? ')' : ''}`;
  return `${neg ? '(' : ''}$${abs.toFixed(0)}${neg ? ')' : ''}`;
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="flex-1 min-w-[140px] px-5 py-4 border-r border-stone-200 last:border-r-0">
      <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-2">{label}</p>
      <p className="text-2xl font-display font-semibold leading-none" style={{ color: accent || C.ink }}>{value}</p>
      {sub && <p className="text-[11px] font-sans text-stone-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">{children}</h3>
      {sub && <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>}
    </div>
  );
}

function AlertBox({ type, title, children }: { type: 'danger' | 'warning' | 'success' | 'info'; title?: string; children: React.ReactNode }) {
  const s = { danger: 'bg-red-50 border-l-red-800 text-red-800', warning: 'bg-amber-50 border-l-amber-600 text-amber-700', success: 'bg-emerald-50 border-l-emerald-700 text-emerald-700', info: 'bg-blue-50 border-l-blue-800 text-blue-800' }[type];
  return (
    <div className={`${s.split(' ')[0]} border-l-[3px] ${s.split(' ')[1]} rounded-sm p-3.5 mb-2.5`}>
      {title && <p className={`font-sans text-[13px] font-bold ${s.split(' ')[2]} mb-1`}>{title}</p>}
      <div className="font-body text-[12px] text-stone-700 leading-relaxed">{children}</div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────

export default function GiftTrackerPage() {
  const [panel, setPanel] = useState<'history' | 'credit' | 'create'>('history');
  const [gifts, setGifts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [_creditLedger, setCreditLedger] = useState<any>(null);
  const [scenarioAnalysis, setScenarioAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Create form state
  const [newGift, setNewGift] = useState({
    donorId: '', recipientId: '', recipientEntityId: '', giftType: 'annual_exclusion',
    giftDate: '', fairMarketValue: 0, annualExclusionApplied: 0,
    taxableAmount: 0, unifiedCreditUsed: 0, form709Filed: false,
    taxYear: new Date().getFullYear(), description: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [giftsRes, summaryRes] = await Promise.all([
        giftsApi.list(),
        giftsApi.summary().catch(() => ({ data: { data: null } })),
      ]);
      setGifts(giftsRes.data.data || []);
      setSummary(summaryRes.data.data);

      // Load members and entities for forms
      const api = (await import('../../api/client')).default;
      try {
        const [memRes, entRes] = await Promise.all([
          api.get('/members'), api.get('/entities'),
        ]);
        setMembers(memRes.data.data?.rows || memRes.data.data || []);
        setEntities(entRes.data.data?.rows || entRes.data.data || []);
      } catch {}
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const loadCreditLedger = async (memberId: string) => {
    setSelectedMember(memberId);
    if (!memberId) { setCreditLedger(null); setScenarioAnalysis(null); return; }
    try {
      const [ledgerRes, sunsetRes] = await Promise.all([
        giftsApi.creditLedger(memberId),
        giftsApi.sunsetAnalysis(memberId),
      ]);
      setCreditLedger(ledgerRes.data.data);
      setScenarioAnalysis(sunsetRes.data.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateGift = async () => {
    try {
      await giftsApi.create(newGift);
      setNewGift({ ...newGift, fairMarketValue: 0, description: '', annualExclusionApplied: 0, taxableAmount: 0, unifiedCreditUsed: 0 });
      loadData();
      setPanel('history');
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this gift record?')) return;
    try { await giftsApi.delete(id); loadData(); } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-[14px] font-sans text-stone-400 animate-pulse">Loading gift tracker...</p></div>;

  const totalExemptionUsed = parseFloat(summary?.total_exemption_used || '0');
  // OBBBA: Federal exemption permanently set at $15M
  const currentExemption = 15000000;
  const remaining = Math.max(0, currentExemption - totalExemptionUsed);
  const usedPct = ((totalExemptionUsed / currentExemption) * 100).toFixed(1);

  const panels = [
    { id: 'history' as const, label: 'Gift History' },
    { id: 'credit' as const, label: 'Credit & Scenarios' },
    { id: 'create' as const, label: 'Record Gift' },
  ];

  return (
    <div>
      <Card className="flex mb-6 overflow-hidden flex-wrap">
        <StatCard label="Exemption Used" value={fmt(totalExemptionUsed)} sub={`Of ${fmt(currentExemption)}`} accent={C.navy} />
        <StatCard label="Remaining" value={fmt(remaining)} sub={`${(100 - parseFloat(usedPct)).toFixed(0)}% capacity`} accent={C.hunter} />
        <StatCard label="Total Gifts" value={summary?.total_gifts || '0'} sub={`${summary?.unique_donors || 0} donors`} />
        <StatCard label="Forms 709 Filed" value={summary?.forms_filed || '0'} sub={parseInt(summary?.forms_needed || '0') > 0 ? `${summary.forms_needed} needed` : 'All current'} accent={parseInt(summary?.forms_needed || '0') > 0 ? C.burgundy : C.hunter} />
      </Card>

      {/* Unified Credit Bar */}
      <Card className="p-6 mb-6">
        <SectionTitle sub="Lifetime gift tax exemption utilization (OBBBA permanent $15M)">Unified Credit Status</SectionTitle>
        <div className="flex justify-between text-[12px] font-sans text-stone-500 mb-2">
          <span>{fmt(totalExemptionUsed)} used</span>
          <span>{fmt(currentExemption)} total</span>
        </div>
        <div className="h-7 bg-stone-200 rounded-sm overflow-hidden relative">
          <div className="h-full rounded-l-sm absolute left-0" style={{ width: `${usedPct}%`, background: C.navy }} />

        </div>
        <div className="flex gap-5 mt-3">
          {[
            { l: 'Used', c: C.navy, v: fmt(totalExemptionUsed) },
            { l: 'Remaining', c: C.hunter, v: fmt(remaining) },
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: x.c }} />
              <span className="text-[11px] font-sans text-stone-500">{x.l}: <strong className="text-stone-800">{x.v}</strong></span>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-1 mb-5 bg-stone-200 rounded-sm p-0.5 w-fit">
        {panels.map((p) => (
          <button key={p.id} onClick={() => setPanel(p.id)}
            className={`px-4 py-1.5 rounded-sm border-none cursor-pointer text-[11px] font-sans font-semibold transition-all duration-150 ${panel === p.id ? 'text-ivory-100' : 'text-stone-500 bg-transparent'}`}
            style={panel === p.id ? { background: C.navy, color: C.ivory } : {}}>{p.label}</button>
        ))}
      </div>

      {/* Gift History */}
      {panel === 'history' && (
        <Card className="overflow-hidden">
          <div className="px-6 pt-5 pb-3"><SectionTitle>Gift History</SectionTitle></div>
          {gifts.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-[14px] font-sans text-stone-500 mb-2">No gifts recorded yet</p>
              <button onClick={() => setPanel('create')} className="px-5 py-2 rounded-sm text-[12px] font-sans font-bold text-ivory-100" style={{ background: C.navy }}>Record First Gift</button>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-ivory-200">
                  {['Year', 'Donor', 'Recipient', 'Type', 'FMV', 'Exclusion', 'Taxable', 'Exemption Used', '709', ''].map(h => (
                    <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gifts.map((g: any, i: number) => (
                  <tr key={g.id} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                    <td className="px-3.5 py-3 text-[13px] font-sans font-semibold text-stone-800 border-b border-stone-200">{g.tax_year}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">{g.donor_name}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">{g.recipient_name || g.recipient_entity_name || '—'}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200 uppercase">{g.gift_type}</td>
                    <td className="px-3.5 py-3 text-[13px] font-mono text-stone-800 border-b border-stone-200">{fmt(parseFloat(g.fair_market_value))}</td>
                    <td className="px-3.5 py-3 text-[13px] font-mono text-stone-600 border-b border-stone-200">{parseFloat(g.annual_exclusion_applied) > 0 ? fmt(parseFloat(g.annual_exclusion_applied)) : '—'}</td>
                    <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: parseFloat(g.taxable_amount) > 0 ? C.navy : C.textLight }}>{parseFloat(g.taxable_amount) > 0 ? fmt(parseFloat(g.taxable_amount)) : '—'}</td>
                    <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: parseFloat(g.unified_credit_used) > 0 ? C.navy : C.textLight }}>{parseFloat(g.unified_credit_used) > 0 ? fmt(parseFloat(g.unified_credit_used)) : '—'}</td>
                    <td className="px-3.5 py-3 text-[10px] font-sans font-bold border-b border-stone-200" style={{ color: g.form_709_filed ? C.navy : C.textLight }}>{g.form_709_filed ? 'Yes' : 'No'}</td>
                    <td className="px-3.5 py-3 border-b border-stone-200">
                      <button onClick={() => handleDelete(g.id)} className="text-[11px] font-sans text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Credit & Sunset */}
      {panel === 'credit' && (
        <div>
          <Card className="p-6 mb-5">
            <SectionTitle>Select Donor</SectionTitle>
            <select value={selectedMember} onChange={(e) => loadCreditLedger(e.target.value)}
              className="w-full max-w-xs px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans text-stone-800">
              <option value="">Select a family member...</option>
              {members.map((m: any) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </select>
          </Card>

          {scenarioAnalysis && (
            <div className="grid grid-cols-2 gap-5">
              <Card className="p-6">
                <SectionTitle>Current Law Status</SectionTitle>
                <div className="space-y-3">
                  {[
                    { l: 'Lifetime Exemption', v: fmt(scenarioAnalysis.currentLaw.exemption), c: C.ink },
                    { l: 'Cumulative Taxable Gifts', v: fmt(scenarioAnalysis.currentLaw.used), c: C.navy },
                    { l: 'Remaining Exemption', v: fmt(scenarioAnalysis.currentLaw.remaining), c: C.hunter },
                    { l: 'Utilization', v: `${scenarioAnalysis.currentLaw.usedPct}%`, c: C.ink },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-stone-200">
                      <span className="text-[12px] font-sans text-stone-600">{r.l}</span>
                      <span className="text-[13px] font-mono font-semibold" style={{ color: r.c }}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-6">
                <SectionTitle>What-If Scenario</SectionTitle>
                <AlertBox type={scenarioAnalysis.planning.atRiskIfReduced > 1000000 ? 'danger' : 'success'} title={scenarioAnalysis.planning.atRiskIfReduced > 1000000 ? 'At-Risk Exemption' : 'Low Sunset Risk'}>
                  {scenarioAnalysis.planning.recommendation}
                </AlertBox>
                <div className="space-y-3 mt-4">
                  {[
                    { l: 'Scenario Exemption', v: fmt(scenarioAnalysis.scenario.exemption), c: C.burgundy },
                    { l: 'At-Risk Under Scenario', v: fmt(scenarioAnalysis.scenario.atRisk), c: C.burgundy },
                    { l: 'Safe to Use', v: fmt(scenarioAnalysis.planning.safeToUse), c: C.hunter },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-stone-200">
                      <span className="text-[12px] font-sans text-stone-600">{r.l}</span>
                      <span className="text-[13px] font-mono font-semibold" style={{ color: r.c }}>{r.v}</span>
                    </div>
                  ))}
                </div>
                {scenarioAnalysis.planning.antiClawbackApplies && (
                  <AlertBox type="info" title="Anti-Clawback Protection">
                    {fmt(scenarioAnalysis.planning.antiClawbackProtected)} in gifts made under the higher exemption are protected by the anti-clawback regulation. These gifts will NOT be recaptured even if the exemption is ever reduced.
                  </AlertBox>
                )}
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Record Gift */}
      {panel === 'create' && (
        <Card className="p-6">
          <SectionTitle>Record a Gift</SectionTitle>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Donor</label>
              <select value={newGift.donorId} onChange={e => setNewGift({ ...newGift, donorId: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                <option value="">Select...</option>
                {members.map((m: any) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Recipient (Person)</label>
              <select value={newGift.recipientId} onChange={e => setNewGift({ ...newGift, recipientId: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                <option value="">Select...</option>
                {members.map((m: any) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Or Recipient (Entity)</label>
              <select value={newGift.recipientEntityId} onChange={e => setNewGift({ ...newGift, recipientEntityId: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                <option value="">Select...</option>
                {entities.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Gift Type</label>
              <select value={newGift.giftType} onChange={e => setNewGift({ ...newGift, giftType: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                {['annual_exclusion', 'taxable', 'gst_direct', 'gst_indirect', 'charitable'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Gift Date</label>
              <input type="date" value={newGift.giftDate} onChange={e => setNewGift({ ...newGift, giftDate: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Tax Year</label>
              <input type="number" value={newGift.taxYear} onChange={e => setNewGift({ ...newGift, taxYear: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Fair Market Value ($)</label>
              <input type="number" value={newGift.fairMarketValue} onChange={e => setNewGift({ ...newGift, fairMarketValue: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Annual Exclusion Applied ($)</label>
              <input type="number" value={newGift.annualExclusionApplied} onChange={e => setNewGift({ ...newGift, annualExclusionApplied: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Taxable Amount ($)</label>
              <input type="number" value={newGift.taxableAmount} onChange={e => setNewGift({ ...newGift, taxableAmount: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Exemption Used ($)</label>
              <input type="number" value={newGift.unifiedCreditUsed} onChange={e => setNewGift({ ...newGift, unifiedCreditUsed: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 px-3 py-2">
                <input type="checkbox" checked={newGift.form709Filed} onChange={e => setNewGift({ ...newGift, form709Filed: e.target.checked })} />
                <span className="text-[12px] font-sans text-stone-600">Form 709 Filed</span>
              </label>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Description</label>
            <input type="text" value={newGift.description} onChange={e => setNewGift({ ...newGift, description: e.target.value })}
              placeholder="e.g., LP Units to Smith Family Trust"
              className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans" />
          </div>
          <button onClick={handleCreateGift} disabled={!newGift.donorId || !newGift.fairMarketValue}
            className="px-6 py-2.5 rounded-sm text-[12px] font-sans font-bold text-ivory-100 disabled:opacity-50" style={{ background: C.navy }}>
            Record Gift
          </button>
        </Card>
      )}
    </div>
  );
}
